/**
 * Una competencia vista desde afuera: fixture y tabla de posiciones.
 *
 * Son las dos pantallas que el incumbente (Web Competition Manager) tiene y
 * que la federación hoy no puede dejar de usar. Van en el MISMO documento del
 * API —una respuesta, un ETag— porque son las dos pestañas de la misma cosa:
 * si fueran dos pedidos con cachés que vencen desfasadas, la tabla y el fixture
 * de al lado podrían contar cosas distintas de la misma fecha.
 *
 * La tabla se muestra con la columna de por qué cada equipo está donde está
 * (PJ, sets, ratios), porque una tabla automática se defiende mostrando de
 * dónde sale, no diciendo "lo calcula el sistema".
 */
import { useT } from '../../i18n/use-t';
import type {
  PublicClient,
  PublicCompetitionResponse,
  PublicFixtureEntry,
  PublicStandingsBlock,
} from '../../lib/public-client';
import {
  publicCompetitionPath,
  publicHref,
  type PublicCompetitionTab,
} from '../../lib/public-router';
import { usePolled } from './hooks';
import { EmptyBlock, MatchCard, Section, interceptable } from './parts';
import { PublicState, Updated } from './PublicState';

export function PublicCompetition({
  client,
  slug,
  base,
  competitionId,
  tab,
  onNavigate,
}: {
  client: PublicClient;
  slug: string;
  base: string;
  competitionId: string;
  tab: PublicCompetitionTab;
  onNavigate: (href: string) => void;
}): JSX.Element {
  const t = useT();
  const state = usePolled<PublicCompetitionResponse>(
    () => client.getCompetition(slug, competitionId),
    [slug, competitionId],
  );

  if (state.data === null || state.data.competition === null) {
    return <PublicState state={state} what={t('publicPortal.competition')} />;
  }

  return (
    <>
      <PublicCompetitionView
        competition={state.data.competition}
        slug={slug}
        base={base}
        tab={tab}
        onNavigate={onNavigate}
      />
      <Updated updatedAt={state.updatedAt} error={state.error} />
    </>
  );
}

export function PublicCompetitionView({
  competition,
  slug,
  base,
  tab,
  onNavigate,
}: {
  competition: NonNullable<PublicCompetitionResponse['competition']>;
  slug: string;
  base: string;
  tab: PublicCompetitionTab;
  onNavigate: (href: string) => void;
}): JSX.Element {
  const t = useT();

  const tabLink = (value: PublicCompetitionTab, label: string): JSX.Element => {
    const href = publicHref(publicCompetitionPath(slug, competition.id, value), base);
    return (
      <a href={href} aria-current={tab === value ? 'page' : undefined} onClick={interceptable(href, onNavigate)}>
        {label}
      </a>
    );
  };

  return (
    <>
      <h1 className="pub-title">{competition.name}</h1>
      <p className="pub-sub">
        {competition.season}
        {competition.description !== null ? ` · ${competition.description}` : ''}
      </p>

      <nav className="pub-tabs">
        {tabLink('fixture', t('publicPortal.fixture'))}
        {tabLink('posiciones', t('publicPortal.standings'))}
      </nav>

      {tab === 'fixture' ? (
        <Fixture slug={slug} base={base} entries={competition.fixture} onNavigate={onNavigate} />
      ) : (
        <Standings blocks={competition.standings} />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Fixture
// ---------------------------------------------------------------------------

/**
 * El calendario agrupado por FECHA (`matchday`), que es como la federación lo
 * publica y como el club lo busca: "¿cuándo juega la 7?".
 */
function Fixture({
  slug,
  base,
  entries,
  onNavigate,
}: {
  slug: string;
  base: string;
  entries: readonly PublicFixtureEntry[];
  onNavigate: (href: string) => void;
}): JSX.Element {
  const t = useT();
  if (entries.length === 0) return <EmptyBlock>{t('publicPortal.noFixture')}</EmptyBlock>;

  const groups = new Map<string, { label: string; entries: PublicFixtureEntry[] }>();
  for (const entry of entries) {
    // Una llave (playoff) se agrupa por ronda y una liga por fecha; el nombre
    // de la ronda lo arma el portal, porque el modelo guarda el número y no
    // "Cuartos de final" (guardar castellano en la base sería el error).
    const key = `${entry.phaseId}#${entry.groupId ?? ''}#${entry.matchday}`;
    const label =
      entry.groupName !== null
        ? `${entry.phaseName} · ${entry.groupName} · ${t('publicPortal.matchday', { matchday: entry.matchday })}`
        : `${entry.phaseName} · ${t('publicPortal.matchday', { matchday: entry.matchday })}`;
    const group = groups.get(key);
    if (group) group.entries.push(entry);
    else groups.set(key, { label, entries: [entry] });
  }

  return (
    <>
      {[...groups.entries()].map(([key, group]) => (
        <Section key={key} title={group.label}>
          <div className="pub-grid">
            {group.entries.map((entry) => (
              <MatchCard
                key={entry.id}
                slug={slug}
                base={base}
                entry={entry}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        </Section>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Tabla de posiciones
// ---------------------------------------------------------------------------

function Standings({ blocks }: { blocks: readonly PublicStandingsBlock[] }): JSX.Element {
  const t = useT();
  if (blocks.length === 0) return <EmptyBlock>{t('publicPortal.noStandings')}</EmptyBlock>;

  return (
    <>
      {blocks.map((block) => (
        <Section
          key={`${block.phaseId}#${block.groupId ?? ''}`}
          title={block.groupName ? `${block.phaseName} · ${block.groupName}` : block.phaseName}
        >
          <div className="pub-table-wrap">
            <table className="pub-table">
              <thead>
                <tr>
                  <th>{t('publicPortal.position')}</th>
                  <th>{t('publicPortal.team')}</th>
                  <th>{t('publicPortal.points')}</th>
                  <th>{t('publicPortal.played')}</th>
                  <th>{t('publicPortal.won')}</th>
                  <th>{t('publicPortal.lost')}</th>
                  <th>{t('publicPortal.setsFor')}</th>
                  <th>{t('publicPortal.setRatio')}</th>
                  <th>{t('publicPortal.pointRatio')}</th>
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row) => (
                  <tr key={row.teamId}>
                    <td>{row.position}</td>
                    <td>{row.name}</td>
                    <td className="strong">
                      {row.points}
                      {row.adjustment !== 0 ? (
                        // La quita por sanción se muestra aparte para que la
                        // fila no parezca un error de cálculo.
                        <span className="pub-note"> ({row.adjustment})</span>
                      ) : null}
                    </td>
                    <td>{row.played}</td>
                    <td>{row.won}</td>
                    <td>{row.lost}</td>
                    <td>
                      {row.setsWon}/{row.setsLost}
                    </td>
                    <td>{ratio(row.setRatio)}</td>
                    <td>{ratio(row.pointRatio)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {block.matchesPending > 0 ? (
            <p className="pub-note">
              {t('publicPortal.pendingMatches', { count: block.matchesPending })}
            </p>
          ) : null}
        </Section>
      ))}
    </>
  );
}

/** `null` = no perdió ningún set (cociente infinito). */
function ratio(value: number | null): string {
  return value === null ? '∞' : value.toFixed(3);
}
